import { Alert, Snackbar, Typography } from "@mui/material";
import React, { useCallback, useRef, useState } from "react";
import "../../tug/testModuleInfoPage.scss";
import TugFrontIcon from "../../../../../assets/images/tugFront.png";
import UserDetailsHeader from "../../../UserDetailsHeader";
import { useNavigate } from "react-router-dom";
import {
  HOW_TO_TEST_TUG_FRONT,
  PERSON_DASHBOARD,
} from "../../../../../routes/routerPath";
import {
  getSearchParams,
  getStoredTestModules,
} from "../../../../../utlis/helpers";
import { useSection } from "../../../../../i18n/useSection";
import VideoRecorder from "../../../../videoRecorder/VideoRecorder";
import UploadVideoPreview from "../../../../videoRecorder/UploadVideoPreview";
import RecordUploadChoice from "../../../../modals/RecordUploadChoice";
import CancelMeasurementButton from "../../CancelMeasurementButton";
import MeasurementSessionStrip from "../../MeasurementSessionStrip";
import HowToTestButton from "../../HowToTestButton";
import {
  completeCurrentSessionModule,
  getSessionModuleRoute,
  prepareModuleForUpload,
} from "../../measurementSession";
import { startMeasurementVideoUpload } from "../../measurementBackgroundUpload";
import { isProdUsAccount } from "../../../../../utlis/measurementModules";
import {
  hasAllProdUsVideos,
  holdProdUsVideo,
} from "../../../../prodUs/prodUsDeferredUpload";
import { PROD_US_UPLOAD } from "../../../../../routes/routerPath";
import { useMeasurementUploadToasts } from "../../measurementUploadNotifications";
import { userInfoFetch } from "../../../../../utlis/commonFunction";
import {
  MAX_VIDEO_DURATION_SECONDS,
  validateCapturedVideo,
  validateUploadedVideo,
} from "../../videoUploadValidation";
import { getModuleName } from "../../../../../utlis/measurementModuleLabels";
import { translate } from "../../../../../utlis/locale";

const TugFront = () => {
  const videoRef = useRef();
  const navigate = useNavigate();
  const storedTest = getStoredTestModules();
  const getTestDetails = storedTest.find((e) => e.moduleId === 4);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const getInfo = JSON.parse(sessionStorage.getItem("testDescription"));
  const { how_to_test, record_upload } = useSection("testinfo");
  const { testUploadAlert } = useSection("common");
  const videoUploadText = useSection("videoUpload");
  const videoHandler = (title) => {
    if (title === how_to_test) {
      navigate(HOW_TO_TEST_TUG_FRONT + `${getSearchParams()}`);
    }
  };

  const showUploadToast = useCallback(({ message, severity }) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity || "success");
    setSnackbarOpen(true);
  }, []);

  useMeasurementUploadToasts(showUploadToast);

  const videoPickHandler = () => {
    sessionStorage.setItem("fromUpload", JSON.stringify(getTestDetails));
    setShowChoiceModal(true);
  };

  const handleRecord = () => {
    setShowChoiceModal(false);
    setShowRecorder(true);
  };

  const handleUpload = () => {
    setShowChoiceModal(false);
    videoRef.current.click();
  };

  const navigateToMetrics = () => {
    const seniorId = userInfoFetch()?.id;
    const params = new URLSearchParams(window.location.search);
    params.set("tab", "metrics");
    if (seniorId) {
      params.set("seniorId", seniorId);
      params.set("seniorUuid", seniorId);
    }
    navigate(`${PERSON_DASHBOARD}?${params.toString()}`);
  };

  const advanceMeasurementSession = () => {
    const { next, isComplete } = completeCurrentSessionModule();

    if (!isComplete && next) {
      const route = getSessionModuleRoute(next.moduleId);
      if (route) {
        prepareModuleForUpload(next);
        navigate(route + `${getSearchParams()}`);
        return;
      }
    }

    navigateToMetrics();
  };

  const handleAcceptedVideo = (file) => {
    const moduleInfo = getTestDetails || getInfo;
    if (!file || !moduleInfo) return;

    sessionStorage.setItem("fromUpload", JSON.stringify(moduleInfo));
    if (isProdUsAccount()) {
      // Prod_US: TUG (Front) is the second test. Keep this video too, then upload both.
      holdProdUsVideo({
        moduleId: 4,
        file,
        moduleInfo,
        personId: userInfoFetch()?.id,
      });
      completeCurrentSessionModule();
      navigate(
        (hasAllProdUsVideos() ? PROD_US_UPLOAD : PERSON_DASHBOARD) +
          getSearchParams(),
        { replace: true },
      );
      return;
    }
    startMeasurementVideoUpload({ file, moduleInfo });
    showUploadToast({
      severity: "info",
      message: translate("testModule", "upload_running", {
        module:
          getModuleName(moduleInfo) ||
          translate("testModule", "video_fallback"),
      }),
    });
    advanceMeasurementSession();
  };

  const handleRecordingComplete = async (file) => {
    const validation = await validateCapturedVideo(file);
    if (!validation.valid) {
      return {
        valid: false,
        message:
          validation.error === "metadata"
            ? videoUploadText.capture_validation_metadata
            : videoUploadText.validation_duration,
      };
    }

    setShowRecorder(false);
    handleAcceptedVideo(file);
    return { valid: true };
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;

    const validation = await validateUploadedVideo(file);
    if (!validation.valid) {
      const messageKey = `validation_${validation.error}`;
      showUploadToast({
        severity: "error",
        message: videoUploadText[messageKey],
      });
      return;
    }

    // Let the user preview and confirm the picked video before uploading.
    setPendingUploadFile(file);
  };

  const handleConfirmUpload = (file) => {
    setPendingUploadFile(null);
    handleAcceptedVideo(file);
  };

  if (pendingUploadFile) {
    return (
      <UserDetailsHeader onBack={() => setPendingUploadFile(null)}>
        <UploadVideoPreview
          file={pendingUploadFile}
          onConfirm={handleConfirmUpload}
          onCancel={() => setPendingUploadFile(null)}
          moduleTitle={getModuleName(getInfo)}
          // Prod_US: this button uploads both videos, so it says so and keeps the speed note.
          {...(isProdUsAccount()
            ? {
                confirmLabel: translate("sts", "upload_now"),
                note: translate("sts", "selected_note"),
              }
            : {})}
        />
      </UserDetailsHeader>
    );
  }

  return (
    <>
      {showRecorder && (
        <VideoRecorder
          onRecordingComplete={handleRecordingComplete}
          onCancel={() => setShowRecorder(false)}
          facingMode="environment"
          maxDuration={MAX_VIDEO_DURATION_SECONDS}
          preferredOrientation="portrait"
        />
      )}

      <RecordUploadChoice
        open={showChoiceModal}
        onClose={() => setShowChoiceModal(false)}
        onRecord={handleRecord}
        onUpload={handleUpload}
      />

      <UserDetailsHeader>
        <div className="tug-test-info-wrapper measurement-intro">
          <MeasurementSessionStrip moduleId={4} />
          <Typography className="title">{getModuleName(getInfo)}</Typography>
          <div className="tug-module-box">
            <div className="tug-module-subbox">
              <img
                src={TugFrontIcon}
                alt="tug-side"
                className="test-img front-img"
              />
              <Typography className="test-description">
                {getInfo?.description}
              </Typography>
              <HowToTestButton
                label={how_to_test}
                onClick={() => videoHandler(how_to_test)}
              />
              <input
                type="file"
                accept=".mp4,.mov,video/mp4,video/quicktime"
                ref={videoRef}
                className="video-upload"
                onChange={handleFileChange}
              />
              <div className="important-text">
                <Typography
                  className="sub-info"
                  component="div"
                  dangerouslySetInnerHTML={{ __html: testUploadAlert }}
                />
              </div>

              <div className="record-upload-btn">
                <button className="button" onClick={() => videoPickHandler()}>
                  {record_upload}
                </button>
              </div>
            </div>
          </div>
          <CancelMeasurementButton />
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{
              zIndex: 10001,
              bottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
            }}
          >
            <Alert
              onClose={() => setSnackbarOpen(false)}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </div>
      </UserDetailsHeader>
    </>
  );
};

export default TugFront;
